import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const MovingSchoolContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: ${props => props.theme.shadows.card};
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const TextContent = styled.div`
  line-height: 1.8;
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  .highlight {
    background: ${props => props.theme.colors.backgroundLight};
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    border-left: 4px solid ${props => props.theme.colors.primary};
  }
`;

const ExamplesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
`;

const ExampleCard = styled.div`
  background: ${props => props.theme.colors.gradients.card};
  padding: 2rem;
  border-radius: 15px;
  
  h3 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    font-size: 1.3rem;
  }
  
  p {
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.textLight};
  }
`;

const ContactSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const ContactCard = styled.div`
  background: ${props => props.theme.colors.gradients.card};
  padding: 3rem 2rem;
  border-radius: 20px;
  text-align: center;
  
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  .contact-details {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    
    .contact-item {
      text-align: center;
      
      .icon {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      
      .label {
        font-size: 0.9rem;
        color: ${props => props.theme.colors.textLight};
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-weight: 600;
        color: ${props => props.theme.colors.text};
        
        a {
          color: ${props => props.theme.colors.primary};
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
`;

const MovingSchool = () => {
  return (
    <MovingSchoolContainer>
      <Helmet>
        <title>"Bewegte Schule" Lernen mit Tieren - Der Ponystall</title>
        <meta name="description" content="Bewegte Schule mit Ponys - Lernen durch Wahrnehmen, Erleben und Bewegung. Ganzheitliche Erfahrungen mit theoretischen Lerninhalten." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            "Bewegte Schule" Lernen mit Tieren
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <ContentCard>
            <SectionTitle>"Bewegte Schule" Lernen mit Tieren</SectionTitle>
            <TextContent>
              <p>
                Lernen gelingt durch Wahrnehmen, Erleben und Bewegung am leichtesten. Ganzheitliche Erfahrungen 
                bleiben im Gedächtnis. Das ist der Sinn der "Bewegten Schule". Durch Lernspiele mit den Ponys 
                können theoretische Lerninhalte begreifbar vermittelt werden.
              </p>
              
              <div className="highlight">
                <p>
                  Gemeinsam sind wir stark.
                </p>
              </div>
              
              <p>
                Individuelle Projekte und Zielsetzung bespreche ich gerne in einem persönlichen Gespräch. 
                Rufen Sie an: 0162 1353794 Marcella Deeken
              </p>
              
              <p>
                Haben Sie Platz im Außenbereich? Dann kommen wir auch zu Ihnen in die Einrichtung um 
                Projekte zu gestalten.
              </p>
            </TextContent>
          </ContentCard>

          <ContentCard>
            <SectionTitle>Beispiele</SectionTitle>
            <ExamplesGrid>
              <ExampleCard>
                <h3>"Lucky lernt lesen"</h3>
                <p>
                  Mit Lernmaterialien wie Bildkarten, Moosgummibuchstaben, Memory können Buchstaben und 
                  Begriffe erfasst werden, mit Stangen oder Seilen Buchstaben legen, auf den Ponys das 
                  Abc transportieren sind nur einige Ideen des Bewegten Lernens.
                </p>
              </ExampleCard>
              
              <ExampleCard>
                <h3>"Farben, Formen, Zahlen"</h3>
                <p>
                  Bälle auf den Ponys transportieren, Sortieren, nach Farben ordnen und Zählen, oder 
                  Verschiedenfarbige Ringe auf Stangen zum Zahlenkönig bringen. Rohre mit Bällen befüllen, 
                  In welches passen am meisten Bälle?
                </p>
              </ExampleCard>
              
              <ExampleCard>
                <h3>"Konzentration und Geschicklichkeit"</h3>
                <p>
                  Ein Geschicklichkeitsparcour mit den Ponys, Riesenmemory oder Ritterspiele. Je nach Alter 
                  und Motorischen Fähigkeiten können die Spiele im Schwierigkeitsgrad variieren und flexibel 
                  gestaltet werden, so das Erfolgserlebnisse für jedes Kind geschaffen werden.
                </p>
              </ExampleCard>
              
              <ExampleCard>
                <h3>"Teamarbeit"</h3>
                <p>
                  Soziales Lernen mit den Ponys. Gemeinsam Aufgaben lösen die alleine nicht zu schaffen sind. 
                  Rücksichtnahme und Akzeptanz, sowie Empathie und ein positiver Umgang miteinander sind 
                  wichtig im täglichen Leben und für die Teamfähigkeit.
                </p>
              </ExampleCard>
            </ExamplesGrid>
          </ContentCard>
        </Container>
      </ContentSection>

      <ContactSection>
        <Container>
          <ContactCard>
            <h3>Kontakt</h3>
            <div className="contact-details">
              <div className="contact-item">
                <div className="icon">📍</div>
                <div className="label">Adresse</div>
                <div className="value">Weidenweg 2, 26683 Saterland</div>
              </div>
              <div className="contact-item">
                <div className="icon">📞</div>
                <div className="label">Telefon</div>
                <div className="value">
                  <a href="tel:01621353794">0162 1353794</a> / <a href="tel:044987522">04498 7522</a>
                </div>
              </div>
              <div className="contact-item">
                <div className="icon">✉️</div>
                <div className="label">E-Mail</div>
                <div className="value">
                  <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a>
                </div>
              </div>
            </div>
          </ContactCard>
        </Container>
      </ContactSection>
    </MovingSchoolContainer>
  );
};

export default MovingSchool;
